import React, {FC, ReactNode, useMemo} from 'react';

import {createSelector} from '../../../../../srcCommon/helpers/reselectHelpers';
import {useAppDispatch, useAppSelector} from '../../../../helpers/appReduxHelpers';
import {SandboxScenariosEnum} from '../../../../helpers/sandbox/sandboxScenarioHelpers';
import {
  BasicActivityModel,
  BasicActivityTypesEnum,
  BasicVisualTypesEnum,
} from '../../../../models/api/activityModel';
import {ActivitySourceTypesEnum} from '../../../../models/api/activitySourceModel';
import {getSetupGuideIsCompleted} from '../../../../store/data/preferences/preferencesSelectors';
import {getTags} from '../../../../store/data/tags/tagsSelectors';
import {
  navigateToFirstTeamInboxAction,
  navigateToSandboxInboxAction,
} from '../../../../store/data/teammates/teammatesActions';
import {useOnboardingModalState} from '../../../app/topBar/onboarding/onboardingContext';
import {useMaybeOpenAdminWizard} from '../sandboxHelpers';
import {RulesAppliedEducationTip} from './rulesAppliedEducationTip';
import {SandboxEducationTipCoordinator} from './sandboxEducationTipCoordinator';

/*
 * Props.
 */

interface RulesAppliedEducationTipCoordinatorProps {
  activity: BasicActivityModel;
  children?: ReactNode;
}

/*
 * Constants.
 */

const eligibleTagAlias = 'vip_customers';

/*
 * Component.
 */

export const RulesAppliedEducationTipCoordinator: FC<RulesAppliedEducationTipCoordinatorProps> = (props) => {
  const dispatch = useAppDispatch();
  const {maybeOpenOnboardingChecklist} = useOnboardingModalState();
  const maybeOpenAdminWizard = useMaybeOpenAdminWizard();
  const hasCompletedAllTasks = useAppSelector(getSetupGuideIsCompleted);

  const onEnd = () => {
    if (!hasCompletedAllTasks) {
      dispatch(navigateToSandboxInboxAction());
    }

    const isWizardOpen = maybeOpenAdminWizard();

    if (!isWizardOpen) {
      if (hasCompletedAllTasks) {
        dispatch(navigateToFirstTeamInboxAction());
      } else {
        maybeOpenOnboardingChecklist();
      }
    }
  };

  // Get the tag ID that was added by a rule.
  const {activity} = props;
  const ruleAddedTagId =
    activity.type === BasicActivityTypesEnum.TAG &&
    activity.source.type === ActivitySourceTypesEnum.RULE &&
    activity.basicVisualType === BasicVisualTypesEnum.TAG
      ? activity.tagId
      : undefined;

  // Determine whether this tag is eligible for the education tip.
  const getIsEligible = useMemo(() => {
    if (ruleAddedTagId === undefined) {
      return () => false;
    }

    return createSelector([getTags], (tags) => {
      const tag = tags[ruleAddedTagId];
      return tag?.alias === eligibleTagAlias;
    });
  }, [ruleAddedTagId]);
  const isEligible = useAppSelector(getIsEligible);

  // As an optimization, render only on tag activities.
  if (ruleAddedTagId === undefined) {
    return <>{props.children}</>;
  }

  return (
    <SandboxEducationTipCoordinator
      conversationId={props.activity.conversationId}
      scenario={SandboxScenariosEnum.SCENARIO3_ACT2}
      step="rules-applied"
      isEligible={isEligible}
      EducationTip={RulesAppliedEducationTip}
      onEnd={onEnd}
    >
      {props.children}
    </SandboxEducationTipCoordinator>
  );
};
